import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { GAP_X, handleSpot, SMALL_H, TILE_H, TILE_W } from './layout.ts';
import { bandFaint, DEVIATION_FOOT, FOOT, footOf, frameOf, isPale, NOT_CONFIGURED_FOOT, ringOf, tileTitle } from './look.ts';
import { earlyFor, stepName, type Band, type BandTone, type TileStep } from './model.ts';

/** A crossed-out name's width: from beside its dot to half way across the gap after its column. */
const DOT_LABEL_W = TILE_W + GAP_X / 2 - (TILE_W / 2 - 22) - (15 + 14 + 8 + 8);

/**
 * A step as every drawing of a tree draws it (design study 10, face A; study 12): a tile, its picture first, its name
 * under it in Title Case and small capitals, a band across its top for who does it (study 10, band B) with the kind's
 * mark in it for a decision, a gate, a milestone and the ends, and along its foot where it stands on a job (study 12,
 * states A). Not configured, it is drawn as any step is, its picture and all, ringed in the warning ochre with "Not
 * configured" at its foot (Jason, 2026-10-07: "clearly highlight unconfigured steps in any tree and always show its
 * icon"), the master's bar along its foot saying how much is set; dashed and pale while it is only offered (a job's next
 * steps, study 12, next A). A deviation on a job (design study 21: a step come late, a gate marked done early) is ringed
 * dashed in bengara, its foot in bengara. A task done in an earlier phase than its step (design study 31, slice 4) is a
 * smaller tile (`SMALL_H`), its picture smaller, its name and band as any tile's, the step it is done for on a line under
 * its name ("For Engineering"), and where it stands at its foot in words as any tile's ("Under way", "Held"). Colour is
 * never the only signal: the band and the foot say it in words. A click
 * opens it wherever it is drawn, the whole tree or a chain opened (Jason: "clicking another node focuses on that one"); a
 * button at its foot does its own thing. While a step is being joined to another (design study 31, slice 5: TreeView's
 * `choosing`), one that cannot be chosen is faint and says why in words, on hover and in its accessible name, and is
 * unavailable: a press on it chooses nothing.
 */

const TONE: Record<BandTone, string> = {
  ink: 'bg-ink text-white',
  slate: 'bg-slate text-white',
  ochre: 'bg-ochre-deep text-white',
  bengara: 'bg-alert text-white',
  grey: 'bg-ink-3 text-white',
  pale: 'bg-inset text-ink-3 border-b border-rule',
};

/** The kind's mark in the band: drawn, never generated. */
export function BandMark({ mark, size = 11 }: { mark: Band['mark']; size?: number }) {
  if (!mark) return null;
  const common = { width: size, height: size, viewBox: '0 0 16 16', 'aria-hidden': true as const, className: 'shrink-0' };
  if (mark === 'gate') {
    return (
      <svg {...common}>
        <path d="M8 1.2 14.8 8 8 14.8 1.2 8z" fill="none" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    );
  }
  if (mark === 'decision') {
    return (
      <svg {...common}>
        <path d="M3 2 14 8 3 14z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    );
  }
  if (mark === 'milestone') {
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="8" cy="8" r="5" fill="currentColor" />
    </svg>
  );
}

export function BandStrip({ band, className = '' }: { band: Band; className?: string }) {
  return (
    <span className={`flex h-[18px] items-center gap-[5px] overflow-hidden whitespace-nowrap px-[7px] ${TONE[band.tone]} ${className}`}>
      <BandMark mark={band.mark} />
      {band.word ? <span className="truncate font-mono text-[9.5px] uppercase tracking-[0.08em]">{band.word}</span> : null}
    </span>
  );
}

/** The tick a done step's foot carries. */
export const Tick = ({ size = 11 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" className="shrink-0">
    <path d="M3 8.5 6.5 12 13 4.5" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);

export function Tile({
  step,
  x,
  y,
  h = TILE_H,
  selected = false,
  near = false,
  faint = false,
  muted = false,
  handles = false,
  actions,
  why = null,
  onOpen,
}: {
  step: TileStep;
  x: number;
  y: number;
  /** Its height on the board: a tile's, or a row of dots'. */
  h?: number;
  /** Found, or the step opened: ringed in ink. */
  selected?: boolean;
  /** Joined to the step opened. */
  near?: boolean;
  faint?: boolean;
  /** Drawn paler, its words as they are (AA): a step ready to start beside the ones under way (Working on). */
  muted?: boolean;
  /** The dot along its foot a line is drawn from (and a decision's ways on): the master's, and NSB's on a job. */
  handles?: boolean;
  /** What a step carries at its foot setting a job up: the filing box. */
  actions?: ReactNode;
  /** While choosing, why it cannot be chosen: said on hover and in its name, and it is unavailable (its press says why, never picks). */
  why?: string | null;
  onOpen?: () => void;
}) {
  const s = step;
  // Unavailable while choosing: still pressed (to say why), never a choice.
  const refused = why ? { 'aria-disabled': true as const } : {};
  if (s.dot) {
    // A step left out (design study 18): a dot in the line, its name crossed out; the line runs on through it to what
    // came after it, or ends at it when nothing did. Clicked, it is shown as it was offered (JobTree).
    const mid = h / 2;
    return (
      <div data-node={s.key} className="pointer-events-none absolute z-[2]" style={{ left: x, top: y, width: TILE_W, height: h }}>
        <span className="absolute w-px bg-ink-3/50" style={{ left: TILE_W / 2, top: -10, height: (s.through ? h + 4 : mid) + 10 }} />
        <button
          type="button"
          aria-label={why ? `${s.words} ${why}` : s.words}
          title={why ?? `${stepName(s.title)}: left out`}
          {...refused}
          onClick={onOpen}
          className={`pointer-events-auto absolute flex items-center gap-2 rounded-[2px] py-3 pl-[15px] pr-2 ${onOpen ? (why ? 'cursor-not-allowed' : 'cursor-pointer') : ''} ${faint ? 'opacity-40' : ''} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus`}
          style={{ left: TILE_W / 2 - 22, top: mid - 22, minHeight: 44 }}
        >
          <span className={`block h-[14px] w-[14px] shrink-0 rounded-full border-[1.5px] ${selected ? 'border-ink bg-ink' : 'border-ink-3 bg-paper hover:border-ink'}`} />
          {/* Kept to its own column and the gap beside it, three lines at most (the whole name on hover and when opened):
              a long one never runs over the next tile or off the page. */}
          <s className="line-clamp-3 text-left font-mono text-[10.5px] uppercase leading-[1.25] tracking-[0.08em] break-words text-ink-3" style={{ maxWidth: DOT_LABEL_W }}>
            {stepName(s.title)}
          </s>
        </button>
      </div>
    );
  }
  const standing = s.ghost ? null : (s.standing ?? null);
  // Come late, or a gate marked done early (design study 21): ringed dashed in bengara, its foot in bengara.
  const deviation = Boolean(s.deviation);
  const frame = frameOf(s);
  const ring = ringOf(s, selected);
  const pale = muted || isPale(s);
  // Where it stands, and, not configured, so on a line of its own (look.ts).
  const foot = footOf(s);
  const unconfigured = foot.some((l) => l.tone === 'unconfigured');
  // Kept to one line under "Not configured", its day kept and who left to its panel, or cut short at the line's end
  // (L-25e): the whole of it on hover, as in its words.
  const cut = unconfigured && foot[0]?.tone === 'standing';
  const key = (e: KeyboardEvent<HTMLDivElement>) => {
    if (onOpen && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onOpen();
    }
  };
  const click = (e: MouseEvent<HTMLDivElement>) => {
    if (onOpen && !(e.target as HTMLElement).closest('button, a, input')) onOpen();
  };
  // A task done early (design study 31, §7, `.tile.half`): smaller, its picture smaller, its name under it as any tile's.
  const small = Boolean(s.early);
  const pic = small ? 'h-[46px] w-[46px]' : 'h-[78px] w-[78px]';
  return (
    <div
      data-node={s.key}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={why ? `${s.words} ${why}` : s.words}
      title={why ?? tileTitle(s, deviation || cut, unconfigured)}
      {...refused}
      onKeyDown={key}
      onClick={onOpen ? click : undefined}
      className={`absolute z-[2] select-none rounded-[2px] text-ink ${frame} ${ring} ${near ? '!border-ink' : ''} ${faint ? 'opacity-40' : ''} ${onOpen ? `${why ? 'cursor-not-allowed' : 'cursor-pointer'} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus` : ''}`}
      style={{ left: x, top: y, width: TILE_W, height: small ? SMALL_H : TILE_H }}
    >
      {s.highlight === 'loose' ? (
        <span className="absolute -top-6 left-0 whitespace-nowrap rounded-[2px] bg-[#e00000] px-1.5 font-mono text-[9.5px] uppercase leading-[18px] tracking-[0.08em] text-white">Nothing comes in</span>
      ) : null}
      {s.band ? <BandStrip band={s.band} className={`absolute -inset-x-px -top-px rounded-t-[2px] ${muted ? 'opacity-40' : bandFaint(s)}`} /> : null}
      <div className={`absolute inset-x-0 grid place-items-center ${small ? 'top-[22px] h-[46px]' : 'top-6 h-[78px]'}`}>
        {s.picture ? (
          <img src={s.picture} alt="" draggable={false} className={`${pic} object-contain ${pale ? 'opacity-50 grayscale' : ''}`} />
        ) : (
          <span className={`grid ${pic} place-items-center rounded-[2px] border border-dashed border-rule text-rule-strong`}>
            <BandMark mark={s.band?.mark ?? null} size={small ? 20 : 28} />
          </span>
        )}
      </div>
      {s.early ? (
        // Its name, then the step it is done for on a line of its own ("For Engineering"), above where it stands at its foot.
        <div className="absolute inset-x-2 top-[72px] flex flex-col items-center">
          <div className={`line-clamp-2 max-h-[34px] text-center text-[14.5px] leading-[17px] small-caps ${pale ? 'text-ink-3' : 'text-ink'}`}>{stepName(s.title)}</div>
          <div className="mt-0.5 max-w-full truncate font-mono text-[10px] uppercase leading-[12px] tracking-[0.05em] text-ink-2">{earlyFor(s.early.stepName)}</div>
        </div>
      ) : (
        <div className={`absolute inset-x-2 top-[106px] line-clamp-2 h-[34px] text-center text-[14.5px] leading-[17px] small-caps ${pale ? 'text-ink-3' : 'text-ink'}`}>{stepName(s.title)}</div>
      )}
      {actions ? (
        <div className="absolute inset-x-1 bottom-[5px] flex h-[26px] items-center justify-center gap-1.5">
          {/* Offered late (study 21: "to add · late"): the one word beside + Add, whatever the phase's name; where it lands
              and from where are in its words and its title. */}
          {deviation && s.foot ? <span className={`font-mono text-[10px] uppercase tracking-[0.05em] ${DEVIATION_FOOT}`}>{s.foot.split(' · ')[0]}</span> : null}
          {actions}
        </div>
      ) : foot.length ? (
        <div className={`absolute inset-x-1 bottom-[7px] flex min-h-5 flex-col items-center justify-center font-mono text-[10px] uppercase leading-[12px] tracking-[0.05em] ${deviation ? DEVIATION_FOOT : standing ? FOOT[standing] : 'text-ink-3'}`}>
          {foot.map((l, i) =>
            l.tone === 'unconfigured' ? (
              // Not configured (Jason, 2026-10-07): a line of its own, in the ring's ochre, under where it stands.
              <span key="unconfigured" className={`whitespace-nowrap font-medium ${NOT_CONFIGURED_FOOT}`}>
                {l.text}
              </span>
            ) : i === 0 ? (
              <span key="standing" className="flex max-w-full items-center gap-[5px] whitespace-nowrap">
                {standing === 'done' ? <Tick /> : null}
                <span className="truncate">{l.text}</span>
              </span>
            ) : (
              <span key="day" className="whitespace-nowrap">
                {l.text}
              </span>
            ),
          )}
        </div>
      ) : null}
      {s.complete && s.configured === false ? (
        <span className="absolute inset-x-2 bottom-[3px] h-[3px] overflow-hidden rounded-full bg-rule" aria-hidden="true">
          <span className="block h-full bg-ink-3" style={{ width: `${s.complete.of ? (100 * s.complete.done) / s.complete.of : 0}%` }} />
        </span>
      ) : null}
      {handles && s.end !== 'end' ? (
        s.options?.length ? (
          s.options.map((o, i) => (
            <span
              key={o.option}
              data-out={s.key}
              data-option={o.option}
              title={`Draw a line from “${o.text}” down to what follows it`}
              className="absolute -bottom-[6px] z-[3] h-[11px] w-[11px] -translate-x-1/2 cursor-crosshair touch-none rounded-full border border-ink bg-paper hover:bg-ink"
              style={{ left: `${handleSpot(i, s.options!.length) * 100}%` }}
            />
          ))
        ) : (
          <span
            data-out={s.key}
            title="Draw a line from here down to what follows it"
            className="absolute -bottom-[6px] left-1/2 z-[3] h-[11px] w-[11px] -translate-x-1/2 cursor-crosshair touch-none rounded-full border border-ink-3 bg-paper hover:border-ink hover:bg-ink"
          />
        )
      ) : null}
      {s.highlight === 'dead' ? (
        <span className="absolute -bottom-6 right-0 whitespace-nowrap rounded-[2px] bg-[#0057ff] px-1.5 font-mono text-[9.5px] uppercase leading-[18px] tracking-[0.08em] text-white">Leads to nothing</span>
      ) : null}
    </div>
  );
}
